import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleApiResponse } from '@/functions';
import { apiClient } from '@/components/api';
import routes from '@/lib/routes';
import type { IPromotion } from '@/interfaces';
import type { Paginated } from '@/lib/interface';

type TParams = {
	onSuccess?: () => void;
};

export const useRequestDeletePromotion = (params?: TParams) => {
	const queryClient = useQueryClient();
	const { isPending, error, mutateAsync } = useMutation({
		onMutate: async (id: string) => {
			await queryClient.cancelQueries({ queryKey: [routes.api.promotions.base] });
			const previous = queryClient.getQueriesData<Paginated<IPromotion>>({
				queryKey: [routes.api.promotions.base],
			});

			queryClient.setQueriesData<Paginated<IPromotion>>(
				{ queryKey: [routes.api.promotions.base] },
				(old) => {
					if (!old?.data) return old;
					const nextData = old.data.filter((promo) => promo._id !== id);
					if (nextData.length === old.data.length) return old;
					const nextItemCount = Math.max(0, (old.pagination?.itemCount ?? nextData.length) - 1);
					const limit = old.pagination?.limit ?? 1;
					const nextPageCount = Math.max(1, Math.ceil(nextItemCount / Math.max(1, limit)));

					return {
						...old,
						data: nextData,
						pagination: old.pagination
							? {
								...old.pagination,
								itemCount: nextItemCount,
								pageCount: nextPageCount,
							}
							: old.pagination,
					};
				}
			);

			return { previous };
		},
		mutationFn: (id: string) =>
			apiClient
				.delete(routes.api.promotions.delete(id))
				.then(handleApiResponse),
		onError: (_err, _vars, ctx) => {
			ctx?.previous?.forEach(([key, data]) => {
				queryClient.setQueryData(key, data);
			});
			window.location.reload();
		},
		onSuccess: () => params?.onSuccess?.(),
	});

	return {
		isLoading: isPending,
		triggerDeletePromotion: (id: string) => mutateAsync(id),
		error,
	};
};
